import { UserAction } from 'Store/User/actions';

export enum LoginView {
  DEFAULT = 'DEFAULT',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILED = 'LOGIN_FAILED',
  NEW_PASSWORD_SENT = 'NEW_PASSWORD_SENT',
  NEW_PASSWORD_FAILED = 'NEW_PASSWORD_FAILED',
  FORGOT_MESSAGE = 'FORGOT_MESSAGE',
}

export enum MenuView {
  INFO = "SHOW_INFO",
  LOGIN = "SHOW_LOGIN",
  CREATE_ACCOUNT = "CREATE_ACCOUNT",
  CREATE_ACCOUNT_SUCCESS = "CREATE_ACCOUNT_SUCCESS",
  CREATE_ACCOUNT_ALREADY_EXISTS = "CREATE_ACCOUNT_ALREADY_EXISTS",
  UPDATE_ACCOUNT = "UPDATE_ACCOUNT",
  UPDATE_ACCOUNT_SUCCESS = "UPDATE_ACCOUNT_SUCCESS",
  UPDATE_PASSWORD = "UPDATE_PASSWORD",
  UPDATE_PASSWORD_SUCCESS = "UPDATE_PASSWORD_SUCCESS",
  UPDATE_PASSWORD_FAILED = "UPDATE_PASSWORD_FAILED",
  ACTION_FAILED = "ACTION_FAILED",
};

export interface IUser {
  isFetchingUser: boolean;
  isFetchingOrders: boolean;
  isLoginUser: boolean;
  loginView: LoginView;
  menuView: MenuView,
  orders: Julius.Website.OrderSmallViewModel[];
  loginInput: Julius.Website.CustomerLoginViewModel;
  loggedInUser:  Julius.Website.CustomerInfoViewModel;
  countryList: Julius.Website.CountryItemViewModel[];
  menuError: string;
  loginError: string;
  newsletterSuccess: boolean;
  newsletterEmail: string;
  locale: string;
}

const getInitialUserState = (): IUser => {
  const sessionStorageCurrentUser = sessionStorage.getItem('CurrentUser');
  if (sessionStorageCurrentUser) {
    try {
      const parsedCurrentUser: IUser = JSON.parse(sessionStorageCurrentUser);
      return parsedCurrentUser;
    }
    catch {
      return initialUserState;
    }
  }
  return initialUserState;
}

const initialUserState: IUser = {
  isFetchingUser: false,
  isFetchingOrders: false,
  isLoginUser: false,
  loginView: LoginView.DEFAULT,
  menuView: MenuView.LOGIN,
  loginInput: { email: '', password: '' },
  loggedInUser: null,
  orders: null,
  countryList: [],
  menuError: null,
  loginError: null,
  newsletterSuccess: false,
  newsletterEmail: null,
  locale: 'sv-SE',
};

export default function user(state: IUser = getInitialUserState(), action: UserAction): IUser {
  switch (action.type) {
    case 'SET_LOGGED_IN_USER':
      const { nextLoginView, nextMenuView } = action;

      const userToSet = {
        ...state,
        isFetchingUser: false,
        loggedInUser: action.user,
        menuView: nextMenuView ? nextMenuView : state.menuView,
        loginView: nextLoginView ? nextLoginView : state.loginView,
        loginInput: nextLoginView === LoginView.LOGIN_FAILED ?
          state.loginInput : initialUserState.loginInput,
      };
      const serializedUserState = JSON.stringify(userToSet);
      sessionStorage.setItem("CurrentUser", serializedUserState);
      return userToSet;

    case 'SET_LOGIN_EMAIL':
      const loginEmailToSet = {
        ...state,
        loginInput: {
          ...state.loginInput,
          email: action.email,
        },
      };
      const serializedLoginEmailToSetState = JSON.stringify(loginEmailToSet);
      sessionStorage.setItem("CurrentUser", serializedLoginEmailToSetState);
      return loginEmailToSet;

    case 'SET_LOGIN_PASSWORD':
      const loginPasswordToSet = {
        ...state,
        loginInput: {
          ...state.loginInput,
          password: action.password,
        },
      };
      const serializedLoginPasswordToSetState = JSON.stringify(loginPasswordToSet);
      sessionStorage.setItem("CurrentUser", serializedLoginPasswordToSetState);
      return loginPasswordToSet;

    case 'CLEAR_LOGIN_INPUT':
      const loginInputToSet = {
        ...state,
        loginInput: initialUserState.loginInput,
      };
      const serializedLoginInputToSetState = JSON.stringify(loginInputToSet);
      sessionStorage.setItem("CurrentUser", serializedLoginInputToSetState);
      return loginInputToSet;

    case 'SET_IS_LOGIN_USER':
      const isLoginUserToSet =  {
        ...state,
        isLoginUser: action.isLoginUser,
        loginInput: action.isLoginUser ? {
          ...state.loginInput,
          email: action.email,
        } : initialUserState.loginInput,
        isFetchingUser: false,
      };
      const serializedIsLoginUserToSetState = JSON.stringify(isLoginUserToSet);
      sessionStorage.setItem("CurrentUser", serializedIsLoginUserToSetState);
      return isLoginUserToSet;

    case 'SET_NEWSLETTER_STATUS':
      const newsLetterStatusToSet = {
        ...state,
        newsletterSuccess: action.isSuccess,
        newsletterEmail: action.email,
      };
      const serializedNewsLetterStatusToSetState = JSON.stringify(newsLetterStatusToSet);
      sessionStorage.setItem("CurrentUser", serializedNewsLetterStatusToSetState);
      return newsLetterStatusToSet;

    case 'SET_LOGIN_VIEW':
      const loginViewToSet = {
        ...state,
        loginView: action.loginView,
        loginError: action.error,
      };
      const serializedLoginViewToSetState = JSON.stringify(loginViewToSet);
      sessionStorage.setItem("CurrentUser", serializedLoginViewToSetState);
      return loginViewToSet;

    case 'SET_MENU_VIEW':
      const menuViewToSet = {
        ...state,
        menuView: action.menuView,
        menuError: action.error,
      };
      const serializedMenuViewToSetState = JSON.stringify(menuViewToSet);
      sessionStorage.setItem("CurrentUser", serializedMenuViewToSetState);
      return menuViewToSet;

    case 'SET_ORDERS':
      const ordersToSet = {
        ...state,
        orders: action.orders,
        isFetchingOrders: false,
      };
      const serializedOrdersToSetState = JSON.stringify(ordersToSet);
      sessionStorage.setItem("CurrentUser", serializedOrdersToSetState);
      return ordersToSet;

    case 'SET_IS_FETCHING_ORDERS':
      const isFetchingOrdersToSet = {
        ...state,
        isFetchingOrders: action.isFetching,
      };
      const serializedIsFetchingOrdersToSetState = JSON.stringify(isFetchingOrdersToSet);
      sessionStorage.setItem("CurrentUser", serializedIsFetchingOrdersToSetState);
      return isFetchingOrdersToSet;

    case 'SET_IS_FETCHING_USER':
      const isFetchingUserToSet = {
        ...state,
        isFetchingUser: action.isFetching,
      };
      const serializedIsFetchingUsersToSetState = JSON.stringify(isFetchingUserToSet);
      sessionStorage.setItem("CurrentUser", serializedIsFetchingUsersToSetState);
      return isFetchingUserToSet;

    case 'SET_COUNTRY_LIST': 
      const countryListToSet = {
        ...state,
        countryList: action.countryList,
      };
      const serializedCountryListToSetState = JSON.stringify(countryListToSet);
      sessionStorage.setItem("CurrentUser", serializedCountryListToSetState);
      return countryListToSet;

    case 'CLEAR_COUNTRY_LIST':
      const clearCountryListInState = {
        ...state,
        countryList: [] as Julius.Website.CountryItemViewModel[],
      };
      const serializedClearCountryListInState = JSON.stringify(clearCountryListInState);
      sessionStorage.setItem("CurrentUser", serializedClearCountryListInState);
      return clearCountryListInState;

    case 'SET_LOCALE':
      const localeToSetInState = {
        ...state,
        locale: action.locale,
      };
      const serializedLocaleToSetState = JSON.stringify(localeToSetInState);
      sessionStorage.setItem("CurrentUser", serializedLocaleToSetState);
      return localeToSetInState;

    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/store/User/reducer.tsx